import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Avatar,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Fade,
  Slide,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  Paper,
  Tooltip,
  Chip
} from '@mui/material';
import { Add, CloudUpload, Delete, Close, FiberManualRecord, Edit } from '@mui/icons-material';
import knowledgeApi from '../api/knowledgeApi';

const KnowledgeBases = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [kbName, setKbName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Fetch user data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        setError('Failed to load user data');
      }
    }
  }, []);

  // Fetch knowledge bases when user is loaded
  useEffect(() => {
    if (user) {
      fetchKnowledgeBases();
    }
  }, [user]);

  const fetchKnowledgeBases = async () => {
    setLoading(true);
    try {
      const data = await knowledgeApi.getAll();
      setKnowledgeBases(data || []);
    } catch (err) {
      setError('Failed to load knowledge bases');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    setSelectedFiles([...event.target.files]);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleCreateKnowledgeBase = async () => {
    setCreating(true);
    setError(null);
    try {
      const totalBytes = selectedFiles.reduce((acc, file) => acc + file.size, 0);
      const newKb = {
        name: kbName,
        size: totalBytes / (1024 * 1024), // Convert to MB
        type: (selectedFiles[0]?.type?.split('/')[1] || 'pdf').toUpperCase(),
        files: selectedFiles,
      };
      await knowledgeApi.create(newKb);
      await fetchKnowledgeBases();
      setOpenAddForm(false);
      setKbName('');
      setSelectedFiles([]);
    } catch (err) {
      setError('Failed to create knowledge base');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKnowledgeBase = async (id) => {
    try {
      await knowledgeApi.delete(id);
      await fetchKnowledgeBases();
    } catch (err) {
      setError('Failed to delete knowledge base');
    }
  };

  const renderAddForm = () => (
    <Fade in timeout={300}>
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <Slide in direction="up" timeout={500}>
          <Box sx={{
            width: isMobile ? '90%' : '500px',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: 3
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Create Knowledge Base
              </Typography>
              <IconButton onClick={() => setOpenAddForm(false)}>
                <Close />
              </IconButton>
            </Box>

            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Knowledge Base Name"
                variant="outlined"
                value={kbName}
                onChange={(e) => setKbName(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ textAlign: 'center' }}>
                <input
                  accept=".pdf,.txt,.csv,.doc,.docx,.json"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={creating}
                />
                <label htmlFor="file-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    sx={{
                      width: '100%',
                      py: 2,
                      borderStyle: 'dashed'
                    }}
                  >
                    Upload Files
                  </Button>
                </label>
              </Box>

              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selected Files ({selectedFiles.length})
                  </Typography>
                  <Box sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2
                  }}>
                    {selectedFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={`${file.name} (${formatFileSize(file.size)})`}
                        onDelete={() => !creating && setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
                        sx={{ m: 0.5 }}
                        disabled={creating}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setOpenAddForm(false)}
                  disabled={creating}
                  sx={{
                    textTransform: 'none'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateKnowledgeBase}
                  disabled={!kbName || selectedFiles.length === 0 || creating}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {creating ? (
                    <>
                      <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                      Creating...
                    </>
                  ) : 'Create'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Slide>
      </Box>
    </Fade>
  );

  return (
    <Box
      width="100%"
      height="calc(100vh - 100px)"
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        backgroundColor: theme.palette.background.default,
        zIndex: 1,
        position: 'sticky',
        top: 0
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 600,
          color: theme.palette.text.primary
        }}>
          Knowledge Bases
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenAddForm(true)}
          sx={{
            borderRadius: 1,
            px: 2,
            py: 1,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Add Knowledge Base
        </Button>
      </Box>

      <Divider sx={{ width: '100%' }} />

      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 2
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: 'calc(100vh - 200px)'
          }}>
            <CircularProgress />
          </Box>
        ) : knowledgeBases.length === 0 ? (
          <Paper sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            m: 2
          }}>
            <CloudUpload sx={{ 
              fontSize: 60,
              color: theme.palette.text.disabled,
              mb: 2
            }} />
            
            <Typography variant="h6" sx={{ mb: 1 }}>
              No knowledge bases yet
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Start by uploading your first knowledge base
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenAddForm(true)}
              sx={{
                borderRadius: 1,
                px: 3,
                py: 1,
                textTransform: 'none'
              }}
            >
              Create Knowledge Base
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {knowledgeBases.map((kb) => (
              <Grid item xs={12} sm={6} md={4} key={kb.Id}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[3],
                      borderColor: theme.palette.primary.light
                    },
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    position: 'relative'
                  }}
                  onClick={() => navigate(`/dashboard/knowledge/edit/${kb.Id}`)}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 1.5
                  }}>
                    <Avatar sx={{ 
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                      width: 40,
                      height: 40,
                      mr: 1.5
                    }}>
                      {kb.Type?.charAt(0) || 'KB'}
                    </Avatar>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {kb.Name}
                      </Typography>
                      
                      <Chip
                        label={kb.Status || 'Processing'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: kb.Status === 'Ready' 
                            ? theme.palette.success.light
                            : theme.palette.warning.light,
                          color: kb.Status === 'Ready' 
                            ? theme.palette.success.dark
                            : theme.palette.warning.dark
                        }}
                        icon={<FiberManualRecord sx={{ fontSize: '0.7rem' }} />}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      Size: {formatFileSize(kb.Size || 0)} • Type: {kb.Type || 'Unknown'}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 'auto'
                  }}>
                    <Typography variant="caption" color="textSecondary">
                      Updated: {kb.LastUpdated ? new Date(kb.LastUpdated).toLocaleDateString() : 'N/A'}
                    </Typography>
                    
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/knowledge/edit/${kb.Id}`);
                          }}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.primary.main
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteKnowledgeBase(kb.Id);
                          }}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.error.main
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity="error"
          sx={{ width: '100%' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      </Snackbar>

      {openAddForm && renderAddForm()}
    </Box>
  );
};

export default KnowledgeBases;